import { useSearchParams } from "react-router-dom";
import { useMemo } from "react";

/**
 * Custom hook for managing persistent filters using URL query parameters
 * @param {Object} defaultFilters - Default filter values
 * @returns {Array} [filters, setFilters] - Current filters and setter function
 */
export function usePersistentFilters(defaultFilters = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Load filters from query params OR fallback to defaults
  const filters = useMemo(() => {
    const result = { ...defaultFilters };
    
    for (const key in defaultFilters) {
      const paramValue = searchParams.get(key);
      
      // Handle different data types
      if (paramValue !== null) {
        // Handle date objects (assuming they're stored as ISO strings)
        if (defaultFilters[key] && typeof defaultFilters[key] === 'object' && defaultFilters[key].from !== undefined) {
          try {
            const dateRange = JSON.parse(paramValue);
            result[key] = {
              from: dateRange.from ? new Date(dateRange.from) : defaultFilters[key].from,
              to: dateRange.to ? new Date(dateRange.to) : defaultFilters[key].to
            };
          } catch {
            result[key] = defaultFilters[key];
          }
        } else {
          result[key] = paramValue;
        }
      }
    }
    
    return result;
  }, [searchParams, defaultFilters]);

  // Update filters and sync with URL
  const setFilters = (newFilters) => {
    const updated = { ...filters, ...newFilters };
    
    // Convert to URL-safe format
    const urlParams = {};
    for (const [key, value] of Object.entries(updated)) {
      if (value !== null && value !== undefined && value !== '') {
        // Handle date objects
        if (typeof value === 'object' && value.from !== undefined) {
          urlParams[key] = JSON.stringify({
            from: value.from?.toISOString?.() || value.from,
            to: value.to?.toISOString?.() || value.to
          });
        } else {
          urlParams[key] = String(value);
        }
      }
    }
    
    setSearchParams(urlParams);
  };

  return [filters, setFilters];
}

export default usePersistentFilters;